/**
 * Surface 1.3 — Capacity forecast. Every figure, week label, person row, tint
 * and destination this screen renders resolves here or in `@/demo/data`; the
 * page component carries no literal.
 *
 * The four headline figures (confirmed 4 210 h · pipeline 1 380 h · available
 * 4 960 h · gap −630 h from W41) are the deck's own numbers for this screen,
 * transcribed as given — see product/px-platform-mockups.md §1.3. They close:
 * 4 960 available − (4 210 confirmed + 1 380 pipeline) = −630.
 *
 * The weekly heat values are authored ONCE below and are anchored to the seed
 * by `heatRow`, which refuses any series whose NOW-week cell disagrees with
 * that person's `loadPct`. The pool page's load column and this table's W35
 * column therefore cannot drift apart.
 */
import {
  GROUP_HEADCOUNT,
  NOW,
  OFFICES,
  PEOPLE,
  heroProject,
  personById,
  thinSpace,
  type OfficeCode,
} from '@/demo/data'

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Destinations ───────────────────────────────────────────────────────── */

export const POOL_HREF = '/resources/pool'
export const ALLOCATIONS_HREF = '/resources/allocations'
export const PIPELINE_HREF = '/resources/pipeline'
export const SKILLS_HREF = '/resources/skills'

export const personHref = (personId: string): string =>
  `/resources/people/${personId}`

/* ── The 12-week window ─────────────────────────────────────────────────── */

/** ISO-8601 week number. Fed by NOW — the prototype never reads the clock. */
const isoWeek = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  )
  const weekday = d.getUTCDay() === 0 ? 7 : d.getUTCDay()
  d.setUTCDate(d.getUTCDate() + 4 - weekday)
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1)
  return Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7)
}

export const FIRST_WEEK = 34
export const WEEK_COUNT = 12
export const LAST_WEEK = FIRST_WEEK + WEEK_COUNT - 1

/** The week NOW falls in — the forecast's "you are here" column. */
export const NOW_WEEK = isoWeek(NOW)

/** The week the Van Dijk migration and the QA wave collide (deck: "from W41"). */
export const SQUEEZE_WEEK = 41

const weekLabel = (week: number): string => `W${week}`
const weekKey = (week: number): string => `w${week}`

/** The in-page anchor the gap KPI points at. */
export const SQUEEZE_ANCHOR_ID = weekKey(SQUEEZE_WEEK)

export interface ForecastWeek {
  /** ISO week number. */
  week: number
  key: string
  label: string
  isNow: boolean
  isSqueeze: boolean
}

export const WEEKS: readonly ForecastWeek[] = Array.from(
  { length: WEEK_COUNT },
  (_, index) => {
    const week = FIRST_WEEK + index
    return {
      week,
      key: weekKey(week),
      label: weekLabel(week),
      isNow: week === NOW_WEEK,
      isSqueeze: week === SQUEEZE_WEEK,
    }
  }
)

if (NOW_WEEK < FIRST_WEEK || NOW_WEEK > LAST_WEEK) {
  throw new Error(`NOW (week ${NOW_WEEK}) falls outside the forecast window`)
}

export const WEEK_RANGE_LABEL = `${weekLabel(FIRST_WEEK)} → ${weekLabel(LAST_WEEK)}`

/** Index of a week within every heat series. */
const weekOffset = (week: number): number => week - FIRST_WEEK

/* ── Load buckets ───────────────────────────────────────────────────────── */

export type BucketKey = 'idle' | 'light' | 'healthy' | 'tight' | 'over'

/** Lower bound of each bucket above `idle`, in % of contracted hours. */
export const BUCKET_FLOORS = {
  light: 40,
  healthy: 65,
  tight: 85,
  over: 100,
} as const

export interface HeatBucket {
  key: BucketKey
  /** Legend range, e.g. "40–65%". */
  range: string
  /** Legend meaning, in the product's voice. */
  label: string
  /** Tint + ink — applied to the cell block AND its legend swatch. */
  className: string
  /** Read after the figure, so a cell never means something by colour alone. */
  srLabel: string
  /** Printed beside the figure when the cell is over-allocated. */
  cellSuffix?: string
}

const IDLE: HeatBucket = {
  key: 'idle',
  range: `Under ${BUCKET_FLOORS.light}%`,
  label: 'idle — capacity going unused',
  className: 'bg-muted text-ink-soft',
  srLabel: 'idle',
}

const LIGHT: HeatBucket = {
  key: 'light',
  range: `${BUCKET_FLOORS.light}–${BUCKET_FLOORS.healthy}%`,
  label: 'light — room to take work on',
  className: 'bg-done-bg text-done',
  srLabel: 'light load',
}

const HEALTHY: HeatBucket = {
  key: 'healthy',
  range: `${BUCKET_FLOORS.healthy}–${BUCKET_FLOORS.tight}%`,
  label: 'healthy — inside the target band',
  className: 'bg-accent text-accent-foreground',
  srLabel: 'healthy load',
}

const TIGHT: HeatBucket = {
  key: 'tight',
  range: `${BUCKET_FLOORS.tight}–${BUCKET_FLOORS.over}%`,
  label: 'tight — no absorption left',
  className: 'bg-review-bg text-review',
  srLabel: 'tight',
}

const OVER: HeatBucket = {
  key: 'over',
  range: `Over ${BUCKET_FLOORS.over}%`,
  label: 'over-allocated — someone slips',
  className: 'bg-blocked-bg text-blocked',
  srLabel: 'over-allocated',
  cellSuffix: 'over',
}

export const BUCKETS: readonly HeatBucket[] = [
  IDLE,
  LIGHT,
  HEALTHY,
  TIGHT,
  OVER,
]

export const bucketFor = (loadPct: number): HeatBucket =>
  loadPct < BUCKET_FLOORS.light
    ? IDLE
    : loadPct < BUCKET_FLOORS.healthy
      ? LIGHT
      : loadPct < BUCKET_FLOORS.tight
        ? HEALTHY
        : loadPct <= BUCKET_FLOORS.over
          ? TIGHT
          : OVER

/* ── The heat table ─────────────────────────────────────────────────────── */

export interface HeatRow {
  personId: string
  name: string
  office: OfficeCode
  role: string
  href: string
  /** One load % per week, W34 → W45, in column order. */
  weekly: readonly number[]
}

/**
 * Authored weekly load, in % of contracted hours, W34 → W45.
 *
 * The shape of each series is the story the screen tells: the three people the
 * pool page already names as over 90% never come down; Julien (FR migration &
 * QA) and Blerta (KS QA) are pulled over 100% from W41 when the Van Dijk
 * migration lands on top of the Lumière load; Arta climbs out of the 40s from
 * W41 because that migration work is exactly what PX proposes moving to her;
 * the two on the bench stay flat and idle through the whole squeeze.
 */
const WEEKLY_LOAD: readonly { personId: string; weekly: readonly number[] }[] =
  [
    {
      personId: 'margot-claes',
      weekly: [88, 96, 96, 96, 98, 94, 96, 104, 108, 102, 98, 92],
    },
    {
      personId: 'pieter-bakker',
      weekly: [90, 94, 96, 98, 96, 94, 96, 100, 98, 92, 88, 86],
    },
    {
      personId: 'hugo-moreau',
      weekly: [86, 92, 92, 90, 88, 90, 94, 102, 106, 98, 90, 86],
    },
    {
      personId: 'anouk-de-vries',
      weekly: [80, 85, 86, 88, 84, 82, 86, 92, 90, 84, 80, 78],
    },
    {
      personId: 'endrit-krasniqi',
      weekly: [78, 82, 84, 82, 80, 82, 86, 94, 96, 88, 80, 76],
    },
    {
      personId: 'blerta-hoxha',
      weekly: [76, 80, 78, 80, 82, 80, 84, 98, 104, 96, 84, 78],
    },
    {
      personId: 'julien-roux',
      weekly: [52, 58, 56, 54, 58, 60, 66, 96, 104, 92, 72, 60],
    },
    {
      personId: 'arta-berisha',
      weekly: [42, 45, 44, 46, 48, 50, 62, 84, 88, 86, 78, 70],
    },
    {
      personId: 'thomas-peeters',
      weekly: [0, 0, 0, 15, 15, 20, 20, 25, 25, 30, 35, 35],
    },
    {
      personId: 'vlora-ahmeti',
      weekly: [0, 0, 0, 0, 10, 10, 15, 15, 20, 25, 30, 35],
    },
  ]

const heatRow = (personId: string, weekly: readonly number[]): HeatRow => {
  const person = personById(personId)
  if (weekly.length !== WEEK_COUNT) {
    throw new Error(
      `${person.name}: forecast needs ${WEEK_COUNT} weeks, got ${weekly.length}`
    )
  }
  const atNow = weekly[weekOffset(NOW_WEEK)]
  if (atNow !== person.loadPct) {
    throw new Error(
      `${person.name}: ${weekLabel(NOW_WEEK)} load ${String(atNow)}% disagrees with the seeded ${person.loadPct}%`
    )
  }
  return {
    personId: person.id,
    name: person.name,
    office: person.office,
    role: person.role,
    href: personHref(person.id),
    weekly,
  }
}

export const HEAT_ROWS: readonly HeatRow[] = WEEKLY_LOAD.map((entry) =>
  heatRow(entry.personId, entry.weekly)
)

const loadAt = (row: HeatRow, week: number): number =>
  row.weekly[weekOffset(week)] ?? 0

/** Offices carrying an over-allocated person at or after the squeeze week. */
export const SQUEEZED_OFFICES: readonly OfficeCode[] = OFFICES.map(
  (office) => office.code
).filter((code) =>
  HEAT_ROWS.some(
    (row) =>
      row.office === code &&
      WEEKS.some(
        (week) =>
          week.week >= SQUEEZE_WEEK &&
          bucketFor(loadAt(row, week.week)).key === 'over'
      )
  )
)

/** Weeks in which a given person is over-allocated. */
const overWeeksFor = (personId: string): readonly string[] => {
  const row = HEAT_ROWS.find((candidate) => candidate.personId === personId)
  if (!row) return []
  return WEEKS.filter(
    (week) => bucketFor(loadAt(row, week.week)).key === 'over'
  ).map((week) => week.label)
}

const andList = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`

/* ── Headline figures (deck) ────────────────────────────────────────────── */

export const DEMAND = {
  confirmedHours: 4_210,
  pipelineHours: 1_380,
  availableHours: 4_960,
  /** Negative — the hours the group is short if the pipeline lands. */
  gapHours: -630,
  /** The confidence band the pipeline figure is filtered to. */
  pipelineConfidencePct: 70,
} as const

const hours = (value: number): string => `${thinSpace(Math.abs(value))}\u202fh`
const signedHours = (value: number): string =>
  value < 0 ? `−${hours(value)}` : hours(value)

export interface CapacityKpi {
  key: string
  label: string
  figure: string
  qualifier: string
  /** The gap is the exception: rose figure, tinted card, anchored to W41. */
  emphasis?: boolean
  anchorHref?: string
}

export const KPIS: readonly CapacityKpi[] = [
  {
    key: 'confirmed',
    label: 'CONFIRMED DEMAND',
    figure: hours(DEMAND.confirmedHours),
    qualifier: `signed work booked across ${WEEK_RANGE_LABEL}`,
  },
  {
    key: 'pipeline',
    label: `PIPELINE DEMAND (${DEMAND.pipelineConfidencePct}%+)`,
    figure: hours(DEMAND.pipelineHours),
    qualifier: 'shadow demand from open deals',
  },
  {
    key: 'available',
    label: 'AVAILABLE CAPACITY',
    figure: hours(DEMAND.availableHours),
    qualifier: `${GROUP_HEADCOUNT} people · ${OFFICES.length} offices`,
  },
  {
    key: 'gap',
    label: 'GAP IF PIPELINE LANDS',
    figure: signedHours(DEMAND.gapHours),
    qualifier: `from ${weekLabel(SQUEEZE_WEEK)} · migration & QA`,
    emphasis: true,
    anchorHref: `#${SQUEEZE_ANCHOR_ID}`,
  },
]

/* ── Copy ───────────────────────────────────────────────────────────────── */

export const CAPACITY_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: `Capacity › ${WEEK_COUNT}-week forecast`,
  contextPill: WEEK_RANGE_LABEL,
} as const

export const CAPACITY_COPY = {
  title: 'Capacity forecast',
  lede: 'Where the group runs tight, and where it runs idle — before either becomes a surprise.',
  tableLabel: `LOAD BY PERSON · % OF CONTRACTED HOURS · ${WEEK_RANGE_LABEL}`,
  tableCaption: `Weekly load per person from ${weekLabel(FIRST_WEEK)} to ${weekLabel(LAST_WEEK)}, as a percentage of contracted hours. Every cell states its figure; the tint repeats it.`,
  personColumn: 'PERSON',
  nowMarker: 'now',
  squeezeMarker: 'squeeze',
  legendLabel: 'HOW TO READ A CELL',
  cellLabel: (loadPct: number): string => `${loadPct}%`,
  personMeta: (office: OfficeCode, role: string): string =>
    `${office} · ${role}`,
  rowLoadSummary: (name: string, weekCount: number): string =>
    `${name} — load for ${weekCount} weeks`,
} as const

/* ── The mint panel: three proposals, each with somewhere to decide ─────── */

const hero = heroProject()
const arta = personById('arta-berisha')
const margot = personById('margot-claes')
const thomas = personById('thomas-peeters')
const vlora = personById('vlora-ahmeti')

const firstName = (name: string): string => name.split(' ')[0] ?? name

/** Hours of the hero migration PX proposes moving off the FR/NL squeeze. */
export const MIGRATION_SHIFT_HOURS = 60

/** Skills only one person in the whole group holds. */
const soleHolderSkills = (personId: string): readonly string[] => {
  const person = personById(personId)
  return person.skills.filter(
    (skill) =>
      PEOPLE.filter((candidate) => candidate.skills.includes(skill)).length ===
      1
  )
}

export const DECISIONS_LABEL = 'PX FLAGS THREE DECISIONS'

export const DECIDE_SUFFIX = {
  arrow: '→ decide in Allocations',
  href: ALLOCATIONS_HREF,
} as const

export interface CapacityDecision {
  key: string
  title: string
  body: readonly Segment[]
  /** Mint furniture: PX proposes, a named human decides. */
  tag: string
}

export const DECISIONS: readonly CapacityDecision[] = [
  {
    key: 'migration-gap',
    tag: 'PROPOSAL',
    title: `${weekLabel(SQUEEZE_WEEK)} migration gap`,
    body: [
      {
        kind: 'text',
        text: `Move ${MIGRATION_SHIFT_HOURS} h of ${hero.client} migration to `,
      },
      { kind: 'link', label: firstName(arta.name), href: personHref(arta.id) },
      { kind: 'text', text: ', or start the Kosovo migration ' },
      { kind: 'link', label: 'cohort', href: PIPELINE_HREF },
      {
        kind: 'text',
        text: ` four weeks early. Either one keeps ${andList(SQUEEZED_OFFICES)} under ${BUCKET_FLOORS.over}% from ${weekLabel(SQUEEZE_WEEK)}. `,
      },
    ],
  },
  {
    key: 'single-point-payroll',
    tag: 'PROPOSAL',
    title: `${firstName(margot.name)} is a single point of failure`,
    body: [
      {
        kind: 'text',
        text: `${margot.name} is the only person in the group holding ${andList(
          soleHolderSkills(margot.id)
        )}, and she is over ${BUCKET_FLOORS.over}% in ${andList(overWeeksFor(margot.id))}. Certify a second holder — the gap is visible in `,
      },
      { kind: 'link', label: 'the skills matrix', href: SKILLS_HREF },
      { kind: 'text', text: '. ' },
    ],
  },
  {
    key: 'bench-capacity',
    tag: 'PROPOSAL',
    title: 'The bench sits out the squeeze',
    body: [
      { kind: 'link', label: thomas.name, href: personHref(thomas.id) },
      { kind: 'text', text: ' and ' },
      { kind: 'link', label: vlora.name, href: personHref(vlora.id) },
      {
        kind: 'text',
        text: ` stay under ${BUCKET_FLOORS.light}% for all ${WEEK_COUNT} weeks while ${andList(SQUEEZED_OFFICES)} run over. Brief them onto migration work now, or the bench is still a bench in ${weekLabel(LAST_WEEK)} — the rest of `,
      },
      { kind: 'link', label: 'the pool', href: POOL_HREF },
      { kind: 'text', text: ' is one click away. ' },
    ],
  },
]

/* ── What the surface withholds ─────────────────────────────────────────── */

export const CAPACITY_DENSITY_NOTE: readonly Segment[] = [
  {
    kind: 'text',
    text: `The table shows ${HEAT_ROWS.length} of ${GROUP_HEADCOUNT} people — the tightest and the idlest, the two ends a hiring decision turns on. The rest of the group stands in `,
  },
  { kind: 'link', label: 'the pool', href: POOL_HREF },
  {
    kind: 'text',
    text: `, and the weeks beyond ${weekLabel(LAST_WEEK)} are withheld until the pipeline confirms them — a forecast the deals have not signed is a guess, not a plan.`,
  },
]
